import Vue from "vue";
import getToken from "./getToken";
import axios from "axios";


let token = '';
let isRefreshing = false;
let refreshPromise = null;

/**
 * Load token from localStorage or request new one
 */
async function getNewToken() {
  const storedToken = localStorage.getItem('accessToken');

  if (storedToken) {
    token = storedToken;
  } else {
    const newToken = await getToken.token();
    if (!newToken) return null;

    token = newToken;
    localStorage.setItem('accessToken', token);
  }

  return token;
}

/**
 * Refresh token safely
 */

async function refreshToken() {
  if (isRefreshing) {
    // If already refreshing, return the same promise
    return refreshPromise;
  }
  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const refreshed = await getToken.refresh(token);
      if (!refreshed) throw new Error("Refresh failed");
      token = refreshed;
      localStorage.setItem('accessToken', token);
      return token;
    } catch (e) {
      // fallback: get completely new token
      try {
        const newToken = await getToken.token();
        if (!newToken) throw new Error("New token failed");
        token = newToken;
        localStorage.setItem('accessToken', token);
        return token;
      } catch (err) {
        // total failure → clear everything
        localStorage.removeItem('accessToken');
        token = '';
        return null;
      }
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}


/**
 * Generic GET handler with automatic retry on 401
 * Queues requests during token refresh for faster recovery
 */
async function getData(url) {
  try {
    const res = await Vue.axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return { data: res.data.data || res.data };
  } catch (err) {
    const status = err.response?.status;
    if (status === 401) {
      // Wait for refresh (queue if already refreshing)
      const newToken = await refreshToken();
      if (!newToken) return null;
      // retry once with new token
      try {
        const retryRes = await Vue.axios.get(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        return { data: retryRes.data.data || retryRes.data };
      } catch {
        return null;
      }
    }
    return null;
  }
}

export default {

  async fetcher(url) {
    await getNewToken();
    if (!token) return null;
    return getData(url);
  },

  async getLinkList(id) {
    await getNewToken();
    if (!token) return null;
    const url = `items/film_links?status=published&filter[film]=${id}`;
    return getData(url).then(res => res ? res.data : null);
  },

  async getVimeoVideoId(url) {
    try {
      const res = await Vue.axios.get(
        `https://vimeo.com/api/oembed.json?url=${url}`
      );
      return res;
    } catch {
      return null;
    }
  },

  async getAllImageUrl(props) {
    await getNewToken();
    if (!token) return null;
    const url = `files?${props}`;
    return getData(url).then(res => res ? res.data : null);
  },

  async getNobelList(limit) {
    await getNewToken();
    if (!token) return [];
    const url = `items/nobel_list?status=published&limit=${limit}&sort=?`;
    return getData(url).then(res => res ? res.data : []);
  },

  async paymentIntent(amount, description) {
    try {
      const res = await axios.post(
        `${process.env.VUE_APP_API_BASE_URL}/api/PeaceJamPayment/peacejam-paymentIntent`,
        { amount, description },
        { headers: { Accept: 'application/json' } }
      );
      return res.data;

    } catch {
      return 'error';
    }
  },

  async createSubscription(amount, email, name, description) {
    try {
      const res = await axios.post(
        `${process.env.VUE_APP_API_BASE_URL}/api/PeaceJamPayment/peacejam-subscription`,
        { amount, email, name, description },
        { headers: { Accept: 'application/json' } }
      );
      return res.data;
    } catch {
      return 'error';
    }
  }
};